/*
 * AdminQuotas — Role-based refund quota management
 *
 * Sections:
 *   1. "My quota" card — current admin's tier + today's usage meter
 *   2. Tier ladder — 4 tiers (config editable inline by directors)
 *   3. Admin assignments — list every admin user + their tier + today's usage
 *   4. Elevation requests — pending requests + decide modal (4-eyes)
 *   5. Audit trail — tier change history
 */
import React, { useEffect, useState } from "react";
import {
  Gauge, Shield, ShieldCheck, ShieldAlert, TrendingUp, AlertTriangle,
  CheckCircle2, XCircle, Loader2, Edit3, Save, ArrowUpCircle, Clock, History,
} from "lucide-react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../components/ui/dialog";
import { PageHeader } from "../../components/PortalShell";
import { toast } from "sonner";


const money = (n) =>
  `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const TIER_TONE = {
  tier_1:         "bg-sky-50 border-sky-200 text-sky-800",
  tier_2:         "bg-indigo-50 border-indigo-200 text-indigo-800",
  tier_3:         "bg-violet-50 border-violet-200 text-violet-800",
  tier_unlimited: "bg-emerald-50 border-emerald-300 text-emerald-800",
};

const TIER_LABEL_SHORT = {
  tier_1: "T1",
  tier_2: "T2",
  tier_3: "T3",
  tier_unlimited: "∞",
};


export default function AdminQuotas() {
  const [me, setMe] = useState(null);
  const [tiers, setTiers] = useState(null);
  const [assignments, setAssignments] = useState(null);
  const [elevations, setElevations] = useState(null);
  const [audit, setAudit] = useState(null);
  const [loading, setLoading] = useState(true);

  // Modals
  const [editTier, setEditTier] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [decideReq, setDecideReq] = useState(null);
  const [elevateOpen, setElevateOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [m, t, a, e, ad] = await Promise.all([
        api.get("/admin/quotas/me"),
        api.get("/admin/quotas/tiers"),
        api.get("/admin/quotas/assignments"),
        api.get("/admin/quotas/elevations"),
        api.get("/admin/quotas/audit?limit=30"),
      ]);
      setMe(m.data);
      setTiers(t.data);
      setAssignments(a.data);
      setElevations(e.data);
      setAudit(ad.data);
    } catch (err) {
      toast.error("Failed to load quotas");
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  if (loading || !me) {
    return (
      <div className="p-12 flex items-center justify-center" data-testid="admin-quotas">
        <Loader2 className="w-5 h-5 animate-spin text-foreground/40"/>
      </div>
    );
  }

  const pendingElev = (elevations?.rows || []).filter(r => r.status === "pending");

  return (
    <div className="p-6 space-y-6" data-testid="admin-quotas">
      <PageHeader
        title="Admin Refund Quotas"
        subtitle="Role-based daily ceilings on refunds + actions · 4-eyes elevation flow · tier audit trail"
      />

      {/* Pending elevation banner */}
      {pendingElev.length > 0 && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-3 flex items-center justify-between" data-testid="quotas-pending-banner">
          <div className="flex items-center gap-2 text-amber-900 text-sm">
            <AlertTriangle className="w-4 h-4"/>
            <span><strong>{pendingElev.length}</strong> elevation request{pendingElev.length === 1 ? "" : "s"} awaiting decision</span>
          </div>
        </div>
      )}

      {/* My quota card */}
      <MyQuotaCard me={me} onRequestElevation={() => setElevateOpen(true)}/>

      {/* Tier ladder */}
      <div>
        <h3 className="text-sm font-semibold mb-2 text-foreground/80 flex items-center gap-2">
          <Shield className="w-3.5 h-3.5"/>Tier ladder
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="quotas-tier-ladder">
          {tiers?.tiers?.map(t => (
            <TierCard key={t.id} tier={t} isCurrent={t.id === me.tier} onEdit={() => setEditTier(t)}/>
          ))}
        </div>
      </div>

      {/* Admin assignments */}
      <div>
        <h3 className="text-sm font-semibold mb-2 text-foreground/80 flex items-center gap-2">
          <ShieldCheck className="w-3.5 h-3.5"/>Admin assignments ({assignments?.count ?? 0})
        </h3>
        <div className="rounded-2xl border bg-background overflow-x-auto" data-testid="quotas-assignments-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-foreground/55 border-b">
                <th className="px-3 py-2 font-medium">Admin</th>
                <th className="px-3 py-2 font-medium">Tier</th>
                <th className="px-3 py-2 font-medium">Today $ refunds</th>
                <th className="px-3 py-2 font-medium">Today actions</th>
                <th className="px-3 py-2 font-medium text-right">Manage</th>
              </tr>
            </thead>
            <tbody>
              {assignments?.rows?.map(r => (
                <tr key={r.admin_id} className="border-b last:border-0" data-testid={`quotas-row-${r.admin_id}`}>
                  <td className="px-3 py-2 text-[12px]">
                    <div className="font-medium">{r.name || r.email}</div>
                    <div className="text-[10px] text-foreground/55">{r.email}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10.5px] border ${TIER_TONE[r.tier] || "bg-foreground/5"}`}>
                      {r.tier_label}
                    </span>
                  </td>
                  <td className="px-3 py-2 tabular-nums text-[12px]">
                    <UsageBar
                      used={r.today_money_usd}
                      pct={r.money_pct_used}
                      formatter={money}
                      tone={r.money_pct_used > 80 ? "warn" : "neutral"}/>
                  </td>
                  <td className="px-3 py-2 tabular-nums text-[12px]">
                    <UsageBar
                      used={r.today_action_count}
                      pct={r.count_pct_used}
                      formatter={(n) => `${n} acts`}
                      tone={r.count_pct_used > 80 ? "warn" : "neutral"}/>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setAssignFor(r)} data-testid={`quotas-assign-${r.admin_id}`}>
                      <Edit3 className="w-3 h-3 mr-1"/>Change tier
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Elevations */}
      <div>
        <h3 className="text-sm font-semibold mb-2 text-foreground/80 flex items-center gap-2">
          <ArrowUpCircle className="w-3.5 h-3.5"/>Elevation requests
          <span className="text-[10px] text-foreground/55 font-normal">
            ({elevations?.summary?.pending || 0} pending · {elevations?.summary?.approved || 0} approved · {elevations?.summary?.denied || 0} denied)
          </span>
        </h3>
        <div className="rounded-2xl border bg-background overflow-x-auto" data-testid="quotas-elevations-table">
          {(elevations?.rows || []).length === 0 ? (
            <div className="px-3 py-6 text-center text-foreground/55 text-sm">
              No elevation requests yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-foreground/55 border-b">
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium">Admin</th>
                  <th className="px-3 py-2 font-medium">Tier change</th>
                  <th className="px-3 py-2 font-medium">Reason</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {(elevations?.rows || []).map(r => (
                  <tr key={r.id} className="border-b last:border-0" data-testid={`elev-row-${r.id}`}>
                    <td className="px-3 py-2 text-[11px] text-foreground/65 whitespace-nowrap">
                      {new Date(r.created_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                    </td>
                    <td className="px-3 py-2 text-[11px]">{r.admin_email}</td>
                    <td className="px-3 py-2 text-[11px]">
                      <span className="font-mono">{r.current_tier}</span> → <span className="font-mono font-semibold">{r.requested_tier}</span>
                    </td>
                    <td className="px-3 py-2 text-[11px] max-w-xs truncate" title={r.reason}>{r.reason}</td>
                    <td className="px-3 py-2">
                      <StatusChip status={r.status}/>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {r.status === "pending" && r.admin_id !== me.admin_id && (
                        <Button size="sm" variant="default" onClick={() => setDecideReq(r)} data-testid={`elev-decide-${r.id}`}>
                          Decide
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Audit */}
      <div>
        <h3 className="text-sm font-semibold mb-2 text-foreground/80 flex items-center gap-2">
          <History className="w-3.5 h-3.5"/>Tier change audit
        </h3>
        <div className="rounded-2xl border bg-background" data-testid="quotas-audit">
          {(audit?.rows || []).length === 0 ? (
            <div className="px-3 py-6 text-center text-foreground/55 text-sm">No tier changes yet.</div>
          ) : (
            <ul className="divide-y">
              {(audit?.rows || []).map(r => (
                <li key={r.id} className="px-3 py-2 text-[11.5px] flex items-center justify-between">
                  <div>
                    <span className="font-mono">{r.prev_tier}</span>
                    <span className="mx-1 text-foreground/55">→</span>
                    <span className="font-mono font-semibold">{r.new_tier}</span>
                    <span className="text-foreground/55"> for </span>
                    <span className="font-medium">{r.admin_id?.slice(0, 8)}</span>
                    <span className="text-foreground/55"> by </span>
                    <span>{r.actor_email}</span>
                  </div>
                  <span className="text-[10px] text-foreground/55 font-mono">
                    {new Date(r.at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <TierEditDialog tier={editTier} onClose={() => setEditTier(null)} onSaved={refresh}/>
      <AssignDialog row={assignFor} availableTiers={assignments?.available_tiers || []} onClose={() => setAssignFor(null)} onSaved={refresh}/>
      <DecideDialog req={decideReq} onClose={() => setDecideReq(null)} onDecided={refresh}/>
      <ElevateDialog open={elevateOpen} me={me} onClose={() => setElevateOpen(false)} onRequested={refresh}/>
    </div>
  );
}


/* ─── Components ──────────────────────────────────────────────────────── */

function MyQuotaCard({ me, onRequestElevation }) {
  const cfg = me.config || {};
  const isUnlimited = me.tier === "tier_unlimited";
  const moneyPct = Math.min(100, me.money_pct_used || 0);
  const countPct = Math.min(100, me.count_pct_used || 0);

  return (
    <div className={`rounded-2xl border p-4 ${TIER_TONE[me.tier] || "bg-background"}`} data-testid="my-quota-card">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <Gauge className="w-5 h-5 mt-0.5"/>
          <div>
            <div className="text-[13px] font-semibold flex items-center gap-2">
              Your tier: {me.tier_label}
              <span className="font-mono text-[10px] px-1.5 py-0.5 bg-background/70 border rounded">
                {TIER_LABEL_SHORT[me.tier]}
              </span>
            </div>
            <div className="text-[11px] opacity-75 mt-0.5">
              {isUnlimited
                ? "Unlimited tier · no daily ceilings · no quota gate"
                : `Daily refund ceiling ${money(cfg.daily_refund_usd)} · ${cfg.daily_action_count} actions · single-action max ${money(cfg.single_action_usd)}`}
            </div>
          </div>
        </div>
        {!isUnlimited && (
          <Button size="sm" variant="outline" onClick={onRequestElevation} data-testid="my-quota-elevate-btn">
            <ArrowUpCircle className="w-3.5 h-3.5 mr-1"/>Request elevation
          </Button>
        )}
      </div>
      {!isUnlimited && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
          <div className="bg-background/70 border rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider opacity-70">$ refunds today</div>
            <div className="text-lg font-semibold tabular-nums mt-0.5">
              {money(me.usage.money_total_usd)}
              <span className="text-[11px] opacity-65 font-normal"> / {money(cfg.daily_refund_usd)}</span>
            </div>
            <ProgressBar pct={moneyPct} tone={moneyPct > 80 ? "warn" : "good"}/>
          </div>
          <div className="bg-background/70 border rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider opacity-70">Actions today</div>
            <div className="text-lg font-semibold tabular-nums mt-0.5">
              {me.usage.action_count}
              <span className="text-[11px] opacity-65 font-normal"> / {cfg.daily_action_count}</span>
            </div>
            <ProgressBar pct={countPct} tone={countPct > 80 ? "warn" : "good"}/>
          </div>
        </div>
      )}
    </div>
  );
}

function TierCard({ tier, isCurrent, onEdit }) {
  const isUnlimited = tier.id === "tier_unlimited";
  return (
    <div className={`rounded-xl border p-3 ${TIER_TONE[tier.id]} ${isCurrent ? "ring-2 ring-offset-2 ring-emerald-500" : ""}`}
         data-testid={`tier-card-${tier.id}`}>
      <div className="flex items-center justify-between">
        <div className="text-[12px] font-semibold">{tier.label}</div>
        {isCurrent && <span className="text-[10px] mono bg-emerald-200/60 border border-emerald-300 px-1.5 py-0.5 rounded">YOURS</span>}
      </div>
      <div className="mt-2 space-y-1.5 text-[11px]">
        <div className="flex justify-between">
          <span className="opacity-70">Daily refund</span>
          <span className="font-mono">{isUnlimited ? "∞" : money(tier.daily_refund_usd)}</span>
        </div>
        <div className="flex justify-between">
          <span className="opacity-70">Daily actions</span>
          <span className="font-mono">{isUnlimited ? "∞" : tier.daily_action_count}</span>
        </div>
        <div className="flex justify-between">
          <span className="opacity-70">Per-action max</span>
          <span className="font-mono">{isUnlimited ? "∞" : money(tier.single_action_usd)}</span>
        </div>
      </div>
      <Button size="sm" variant="ghost" className="w-full mt-3 h-7 text-[11px]" onClick={onEdit} data-testid={`tier-edit-${tier.id}`}>
        <Edit3 className="w-3 h-3 mr-1"/>Edit config
      </Button>
    </div>
  );
}

function UsageBar({ used, pct, formatter, tone }) {
  const cls = tone === "warn" ? "bg-amber-400" : "bg-emerald-400";
  return (
    <div className="space-y-1">
      <div>{formatter(used)}</div>
      <div className="h-1 w-full bg-foreground/10 rounded-full overflow-hidden">
        <div className={`h-full ${cls}`} style={{ width: `${Math.min(100, pct)}%` }}/>
      </div>
    </div>
  );
}

function ProgressBar({ pct, tone }) {
  const cls = tone === "warn" ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="h-1.5 w-full bg-foreground/10 rounded-full overflow-hidden mt-1.5">
      <div className={`h-full ${cls} transition-all`} style={{ width: `${pct}%` }}/>
    </div>
  );
}

function StatusChip({ status }) {
  const tone = status === "approved" ? "bg-emerald-100 text-emerald-700 border-emerald-200"
            : status === "denied"   ? "bg-rose-100 text-rose-700 border-rose-200"
            : "bg-amber-100 text-amber-800 border-amber-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10.5px] border ${tone}`}>
      {status}
    </span>
  );
}


/* ─── Dialogs ────────────────────────────────────────────────────────── */

function TierEditDialog({ tier, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (tier) {
      setForm({
        daily_refund_usd:   tier.daily_refund_usd,
        daily_action_count: tier.daily_action_count,
        single_action_usd:  tier.single_action_usd,
      });
    }
  }, [tier]);

  if (!tier) return null;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/quotas/tiers/${tier.id}`, {
        daily_refund_usd:   Number(form.daily_refund_usd),
        daily_action_count: Number(form.daily_action_count),
        single_action_usd:  Number(form.single_action_usd),
      });
      toast.success(`Tier ${tier.id} updated`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update tier");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!tier} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="tier-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit {tier.label}</DialogTitle>
          <DialogDescription>Adjust daily ceilings + per-action max for this tier. Applies to every admin assigned to {tier.id}.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label>Daily refund $ ceiling</Label>
            <Input type="number" value={form.daily_refund_usd}
              onChange={(e) => setForm({ ...form, daily_refund_usd: e.target.value })}
              data-testid="tier-edit-daily-refund"/>
          </div>
          <div>
            <Label>Daily action count</Label>
            <Input type="number" value={form.daily_action_count}
              onChange={(e) => setForm({ ...form, daily_action_count: e.target.value })}
              data-testid="tier-edit-action-count"/>
          </div>
          <div>
            <Label>Per-action max $</Label>
            <Input type="number" value={form.single_action_usd}
              onChange={(e) => setForm({ ...form, single_action_usd: e.target.value })}
              data-testid="tier-edit-single-max"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="tier-edit-save">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin"/>}
            <Save className="w-3.5 h-3.5 mr-1"/>Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssignDialog({ row, availableTiers, onClose, onSaved }) {
  const [tier, setTier] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (row) { setTier(row.tier); setReason(""); }
  }, [row]);

  if (!row) return null;

  const save = async () => {
    if (!reason.trim() || reason.trim().length < 3) {
      toast.error("Reason required (min 3 chars)"); return;
    }
    setBusy(true);
    try {
      await api.post("/admin/quotas/assignments", {
        admin_id: row.admin_id, tier, reason: reason.trim(),
      });
      toast.success(`Tier updated to ${tier}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to assign tier");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="assign-dialog">
        <DialogHeader>
          <DialogTitle>Change tier for {row.email}</DialogTitle>
          <DialogDescription>Every tier change is audit-logged and surfaces in /admin/overrides.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label>Tier</Label>
            <select className="w-full h-9 border border-input rounded-md px-2 text-sm bg-background"
              value={tier} onChange={(e) => setTier(e.target.value)} data-testid="assign-tier-select">
              {availableTiers.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <Label>Reason <span className="text-rose-600">*</span></Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this tier change needed?" data-testid="assign-reason"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy || reason.trim().length < 3} data-testid="assign-save">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin"/>}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DecideDialog({ req, onClose, onDecided }) {
  const [decision, setDecision] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (!req) return null;

  const submit = async () => {
    if (!decision) { toast.error("Pick approve or deny"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/quotas/elevations/${req.id}/decide`,
                       { decision, reason });
      toast.success(`Request ${decision === "approve" ? "approved" : "denied"}`);
      onDecided();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Decision failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!req} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="decide-dialog">
        <DialogHeader>
          <DialogTitle>Decide elevation request</DialogTitle>
          <DialogDescription>
            <strong>{req.admin_email}</strong> requested <span className="font-mono">{req.current_tier} → {req.requested_tier}</span>.
            Reason: "{req.reason}"
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex gap-2">
            <button onClick={() => setDecision("approve")}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium flex items-center justify-center gap-1.5 ${
                decision === "approve" ? "bg-emerald-100 border-emerald-400 text-emerald-800" : "bg-background border-input"
              }`}
              data-testid="decide-approve">
              <CheckCircle2 className="w-3.5 h-3.5"/>Approve
            </button>
            <button onClick={() => setDecision("deny")}
              className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium flex items-center justify-center gap-1.5 ${
                decision === "deny" ? "bg-rose-100 border-rose-400 text-rose-800" : "bg-background border-input"
              }`}
              data-testid="decide-deny">
              <XCircle className="w-3.5 h-3.5"/>Deny
            </button>
          </div>
          <div>
            <Label>Decision reason (optional)</Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Why this decision?" data-testid="decide-reason"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !decision} data-testid="decide-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin"/>}Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ElevateDialog({ open, me, onClose, onRequested }) {
  const [tier, setTier] = useState("tier_2");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const allTiers = ["tier_1", "tier_2", "tier_3", "tier_unlimited"];
  const higher = allTiers.filter(t => allTiers.indexOf(t) > allTiers.indexOf(me.tier));

  const submit = async () => {
    if (!reason.trim() || reason.trim().length < 3) {
      toast.error("Reason required (min 3 chars)"); return;
    }
    setBusy(true);
    try {
      await api.post("/admin/quotas/elevations", {
        requested_tier: tier, reason: reason.trim(),
      });
      toast.success("Elevation request submitted · awaiting senior admin");
      onRequested();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Request failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="elevate-dialog">
        <DialogHeader>
          <DialogTitle>Request tier elevation</DialogTitle>
          <DialogDescription>
            A senior admin (4-eyes) must approve. Once approved, your tier is upgraded for the rest of the day.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label>Requested tier</Label>
            <select className="w-full h-9 border border-input rounded-md px-2 text-sm bg-background"
              value={tier} onChange={(e) => setTier(e.target.value)} data-testid="elevate-tier-select">
              {higher.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <Label>Reason <span className="text-rose-600">*</span></Label>
            <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Why do you need a higher tier today? (e.g. customer issue requires $2K refund)"
              data-testid="elevate-reason"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 3} data-testid="elevate-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin"/>}
            <ArrowUpCircle className="w-3.5 h-3.5 mr-1"/>Submit request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
